import React, { useState, useEffect } from "react";
import {
  ChevronDown,
  Search,
  Mail,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from "lucide-react";
import "../Styles/attendees.css";

const API_BASE_URL = "";

export default function AttendeesDashboard() {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selected, setSelected] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [query, setQuery] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(20);

  const [sendingId, setSendingId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  const fetchAttendees = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/attendees`);
      if (!res.ok) throw new Error("Failed to load attendees");
      const data = await res.json();
      setAttendees(data);
      setError(null);
    } catch (err) {
      console.error(err);
      setError("Could not load attendees. Is the backend running?");
      setAttendees([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendees();
  }, []);

  const filteredAttendees = attendees.filter((a) =>
    a.fullName?.toLowerCase().includes(query.toLowerCase())
  );

  const toggleAll = () => {
    setSelectAll((v) => !v);
    setSelected(selectAll ? [] : filteredAttendees.map((a) => a.id));
  };

  const toggleOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSendCertificate = async (id) => {
    setSendingId(id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/attendees/${id}/send-certificate`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to send certificate");
      await fetchAttendees();
    } catch (err) {
      console.error(err);
      alert("Failed to send certificate. Please try again.");
    } finally {
      setSendingId(null);
    }
  };

  const handleSendCertificateToAll = async () => {
    if (!window.confirm("Send certificates to all attendees?")) return;
    setSendingAll(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/attendees/send-certificate-all`, {
        method: "POST",
      });
      if (!res.ok) throw new Error("Failed to send certificates");
      await fetchAttendees();
    } catch (err) {
      console.error(err);
      alert("Failed to send certificates to all. Please try again.");
    } finally {
      setSendingAll(false);
    }
  };

  const total = attendees.length;
  const certificateSentCount = attendees.filter((a) => a.certificateSent).length;
  const unsentCount = total - certificateSentCount;

  return (
    <div className="atd-app atd-app--no-sidebar">
      <main className="atd-main">
        <section className="atd-panel">
          <div className="atd-stats">
            <label className="atd-pill atd-pill--entries">
              Show Entries :
              <select
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>

            <button
              className="atd-pill atd-pill--action"
              onClick={handleSendCertificateToAll}
              disabled={sendingAll}
            >
              <Mail size={14} />
              {sendingAll ? "Sending..." : "Certificates Sent to All"}
            </button>

            <span className="atd-pill atd-pill--outline atd-pill--amber-outline">
              Total Attendees : {total}
            </span>
            <span className="atd-pill atd-pill--outline atd-pill--green-outline">
              Certificate Sent : {certificateSentCount}
            </span>
            <span className="atd-pill atd-pill--outline atd-pill--red-outline">
              Unsent : {unsentCount}
            </span>

            <div className="atd-search">
              <Search size={14} />
              <input
                placeholder="Search..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          <div className="atd-table-wrap">
            {loading ? (
              <p className="atd-state-msg">Loading attendees...</p>
            ) : error ? (
              <p className="atd-state-msg atd-state-msg--error">{error}</p>
            ) : (
              <table className="atd-table">
                <thead>
                  <tr>
                    <th className="atd-checkbox-col">
                      <input type="checkbox" checked={selectAll} onChange={toggleAll} />
                      <span>Select All</span>
                    </th>
                    <th>S.No</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Company</th>
                    <th>Contact</th>
                    <th>Personal Email</th>
                    <th>Official Email</th>
                    <th>Check-In Time</th>
                    <th>Certificate Status</th>
                    <th>Certificate Sent</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAttendees.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="atd-state-msg">
                        No attendees yet.
                      </td>
                    </tr>
                  ) : (
                    filteredAttendees.slice(0, entriesPerPage).map((a, index) => (
                      <tr key={a.id}>
                        <td className="atd-checkbox-col">
                          <input
                            type="checkbox"
                            checked={selected.includes(a.id)}
                            onChange={() => toggleOne(a.id)}
                          />
                        </td>
                        <td>{index + 1}</td>
                        <td>
                          <div className="atd-user-cell">
                            <span className="atd-avatar">{a.fullName?.charAt(0)}</span>
                            {a.fullName}
                          </div>
                        </td>
                        <td>{a.designation}</td>
                        <td>{a.company}</td>
                        <td>{a.contact}</td>
                        <td>{a.personalEmail}</td>
                        <td>{a.officialEmail}</td>
                        <td>{a.checkInTime}</td>
                        <td>
                          {a.certificateSent ? (
                            <span className="atd-status-pill atd-status-pill--sent">
                              <span className="atd-status-icon atd-status-icon--sent">&#10003;</span>
                              <span>Sent</span>
                            </span>
                          ) : (
                            <span className="atd-status-pill atd-status-pill--unsent">
                              <span className="atd-status-icon atd-status-icon--unsent">&#10005;</span>
                              <span>Unsent</span>
                            </span>
                          )}
                        </td>
                        <td>
                          <button
                            className="atd-send-cert-btn"
                            disabled={sendingId === a.id}
                            onClick={() => handleSendCertificate(a.id)}
                          >
                            {sendingId === a.id ? "Sending..." : "Sent Certificate"}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>

          <div className="atd-table-footer">
            <span>
              Showing {Math.min(filteredAttendees.length, entriesPerPage)} of {total} Attendees
            </span>
            <div className="atd-pagination">
              <button className="atd-page-btn">
                <ChevronLeft size={14} />
              </button>
              <button className="atd-page-btn active">1</button>
              <button className="atd-page-btn">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}